import { useState } from 'react';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import LtiContentChooser from '@/components/lti/LtiContentChooser';
import { useLtiContentOptions } from '@/hooks/useLtiContentOptions';
import { useMapResourceLink, useUnmappedLtiLinks } from '@/hooks/useLti';
import { LtiResourceType, type LtiUnmappedLink } from '@/types/lti.types';

/** Choice an administrator has made for one pending link, before saving. */
interface Draft {
  type: LtiResourceType;
  resourceId: string;
}

const relativeTime = (iso: string): string => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} d ago`;
};

/** Links an LMS has launched that are not connected to Skillzen content yet. */
export default function UnmappedLtiLinks({ canWrite }: { canWrite: boolean }) {
  const { data: links = [], isLoading } = useUnmappedLtiLinks();
  const mapMutation = useMapResourceLink();
  // Only fetch a catalogue when there is actually something to connect.
  const catalogue = useLtiContentOptions(
    links.length > 0 ? LtiResourceType.COURSE : undefined,
  );

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  if (isLoading || links.length === 0) {
    return null; // nothing outstanding — stay out of the way
  }

  const draftFor = (link: LtiUnmappedLink): Draft =>
    drafts[link._id] ?? { type: LtiResourceType.COURSE, resourceId: '' };

  const save = (link: LtiUnmappedLink) => {
    const draft = draftFor(link);
    if (!draft.resourceId) {
      toast.error('Choose what this link should open.');
      return;
    }

    setSavingId(link._id);
    mapMutation.mutate(
      {
        issuer: link.issuer,
        resource_link_id: link.resource_link_id,
        skillzen_resource_type: draft.type,
        skillzen_resource_id: draft.resourceId,
      },
      {
        onSettled: () => setSavingId(null),
        onSuccess: () =>
          setDrafts(prev => {
            const next = { ...prev };
            delete next[link._id];
            return next;
          }),
      },
    );
  };

  return (
    <div className="card border-amber-200 bg-amber-50/40 mb-6">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <h2 className="font-semibold text-gray-900">
            {links.length} link{links.length === 1 ? '' : 's'} waiting to be connected
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">
            Someone opened these from their LMS, but Skillzen does not know what they should
            show. Choose the content for each one — learners who click it will land there.
          </p>
        </div>
      </div>

      <ul className="space-y-3">
        {links.map(link => {
          const draft = draftFor(link);
          const saving = savingId === link._id;

          return (
            <li
              key={link._id}
              className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col lg:flex-row lg:items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-gray-900 font-medium truncate">
                  <Link2 className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="truncate">
                    {link.context_title || 'Unknown course'}
                    {link.resource_link_title && (
                      <span className="text-gray-400 font-normal"> › {link.resource_link_title}</span>
                    )}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {link.issuer} · {link.launch_count} launch
                  {link.launch_count === 1 ? '' : 'es'} · last {relativeTime(link.last_seen_at)}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 lg:shrink-0">
                <LtiContentChooser
                  type={draft.type}
                  resourceId={draft.resourceId}
                  onChange={next =>
                    setDrafts(prev => ({
                      ...prev,
                      [link._id]: { type: next.type, resourceId: next.resourceId },
                    }))
                  }
                  catalogue={catalogue}
                  disabled={!canWrite || saving}
                />

                <button
                  type="button"
                  onClick={() => save(link)}
                  disabled={!canWrite || saving || !draft.resourceId}
                  className="btn-primary text-sm whitespace-nowrap disabled:opacity-50 flex items-center gap-2 justify-center"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving…' : 'Connect'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
